import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';

import { useSidePanelCutoutsTab } from './hooks/useSidePanelCutoutsTab';

import { useDrawingShapeContext } from '~/legacy/easy-estimator-page/context';
import { useAppDispatch } from '~/redux/hooks';
import { resetActiveDrawingObject } from '~/redux/slices/drawingShape';
import ItemCountAdjustButton from '~atoms/ItemCountAdjustButton';
import DesignStepContainer from '~molecules/DesignStepContainer';
import DesignSectionCard from '~organisms/DesignSectionCard';
import DesignSectionHeader from '~organisms/DesignSectionHeader';
import RemoveAllSelectedProductsModal from '~organisms/RemoveAllSelectedProductsModal';
import RemoveApplianceModal from '~organisms/RemoveApplianceModal';

const removeApplianceModalDescription =
  'DESIGN_TOOLBAR.REMOVE_MODAL.DESCRIPTION_CUTOUT';

const SidePanelCutoutsTab = () => {
  const { t } = useTranslation();

  const {
    cutouts,
    cutoutIdToBeRemoved,
    decreaseOption,
    displayDataById,
    getQty,
    handleClick,
    handleDragEnd,
    increaseOption,
    isFetching,
    modalForOptionRemoval,
    resetCustomCutout,
    selectedCutoutId,
    clearCutoutIdToBeRemoved,
    updateCutoutQuantity,
  } = useSidePanelCutoutsTab();

  const optionName = { option: t('COMMON.CUTOUT') };

  const {
    getters: { activeDrawingAppliance },
  } = useDrawingShapeContext();

  const dispatch = useAppDispatch();

  return isFetching ? (
    <Spin style={{ fontSize: '24rem' }} />
  ) : (
    <>
      <DesignSectionHeader
        title={t('SCROLLABLE_AREA.CUTOUTS.TITLE').toUpperCase()}
        description={t('SCROLLABLE_AREA.CUTOUTS.DESCRIPTION')}
        subtitleStyle={{ fontSize: '16rem' }}
      />

      {cutouts.length > 0 && (
        <DesignStepContainer>
          {cutouts.map((cutout) => {
            const cutoutId = cutout.productId;
            const qty = getQty(cutoutId);
            const display = displayDataById[cutoutId];
            const isSelected = selectedCutoutId === cutoutId;
            const isUnpolished = cutout.abbrevDesc
              .toLowerCase()
              .includes('unpolished');

            return (
              <DesignSectionCard
                style={{ width: '100%' }}
                key={cutoutId}
                title={display.title}
                image={display.image}
                isSelected={qty.all > 0 || isSelected}
                tooltipText={
                  isUnpolished
                    ? t('SELECT', optionName)
                    : t('DESIGN_TOOLBAR.DRAG_ONTO_DESIGN')
                }
                onClick={handleClick(cutoutId)}
                dndArgs={
                  isUnpolished
                    ? undefined
                    : {
                        appliance: cutout,
                        onDragEnd: handleDragEnd,
                      }
                }
              >
                <p>{display.price}</p>
                <ItemCountAdjustButton
                  quantity={qty.all || undefined}
                  onIncrease={increaseOption(cutoutId)}
                  onDecrease={decreaseOption(cutoutId)}
                  onUpdateValue={updateCutoutQuantity(cutoutId)}
                />
              </DesignSectionCard>
            );
          })}
        </DesignStepContainer>
      )}

      <RemoveAllSelectedProductsModal
        description={t(
          'SCROLLABLE_AREA.CUTOUTS.REMOVE_ALL_SELECTED_CUTOUT_DESCRIPTION'
        )}
        isOpen={!!cutoutIdToBeRemoved}
        title={t('DESIGN_TOOLBAR.REMOVE_MODAL.TITLE', {
          option: t('COMMON.CUTOUT'),
        })}
        onClose={clearCutoutIdToBeRemoved}
        onConfirm={() => {
          if (
            cutoutIdToBeRemoved &&
            cutoutIdToBeRemoved ===
              activeDrawingAppliance?.applianceModel.productId
          ) {
            resetCustomCutout(cutoutIdToBeRemoved);
            clearCutoutIdToBeRemoved();
            dispatch(resetActiveDrawingObject());
          }
        }}
      />

      <RemoveApplianceModal
        title={t('DESIGN_TOOLBAR.REMOVE_MODAL.TITLE', optionName)}
        description={t(removeApplianceModalDescription)}
        isOpen={modalForOptionRemoval.isOpen}
        onClose={modalForOptionRemoval.close}
      />
    </>
  );
};

export default SidePanelCutoutsTab;
